import React, { useState } from 'react';
import { bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { get } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Paper from '@material-ui/core/Paper';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import BobPhoneInput from '../../components/BobPhoneInput';

import EsmsDialog from '../../components/EsmsDialog';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import GoogleLogo from '../../components/GoogleLogo';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    contactName: Yup.string()
        .required(),
    title: Yup.string()
        .required(),
    email: Yup.string()
        .email()
        .required(),
    mobilePhone: Yup.string()
        .required(),
    officePhone: Yup.string(),
    address1:    Yup.string()
        .required(),
    address2: Yup.string(),
    city:     Yup.string()
        .required(),
    state: Yup.string()
        .required(),
    zip: Yup.string()
        .required()
});

const CreateContactModal = ({
    close,
    clearErrors,
    open,
    title,
    selectedNode,
    isLoading,
    errors,
    toggleLoading,
    ...props
}) => {

    // eslint-disable-next-line no-unused-vars
    const [address, setAddress] = useState({});
    const [mobilePhoneCountry, setMobilePhoneCountry] = useState();
    const [officePhoneCountry, setOfficePhoneCountry] = useState();

    const formik = useFormik({
        initialValues: {
            contactName:   '',
            title:         '',
            email:         '',
            mobilePhone:   '',
            officePhone:   '',
            address1:      '',
            address2:      '',
            city:          '',
            state:         '',
            zip:           '',
            addressSearch: ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            if (mobilePhoneCountry && mobilePhoneCountry.dialCode === values.mobilePhone) {
                values.mobilePhone = '';
            }

            if (officePhoneCountry && officePhoneCountry.dialCode === values.officePhone) {
                values.officePhone = '';
            }

            const {
                createContact
            } = props;

            let isCreated;

            const body = {
                mailingAddress: {
                    address1: values.address1,
                    address2: values.address2,
                    city:     values.city,
                    state:    values.state,
                    zipcode:  values.zip
                },
                email:       values.email,
                mobilePhone: values.mobilePhone,
                name:        values.contactName,
                officePhone: values.officePhone,
                title:       values.title,
                address:     null
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createContact(selectedNode.id, body);
                }

                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        formik.setFieldValue('addressSearch', '');
        clearErrors();

        close();
    };

    const handleGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3.long_name;

            formik.setFieldValue('addressSearch', results[0].formatted_address);
            formik.setFieldValue('address1', address1Check);
            formik.setFieldValue('city', cityCheck);
            formik.setFieldValue('state', state.short_name);
            formik.setFieldValue('zip', zip.long_name);   

            setAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    const handlePhoneNoChange = (value, key) => {
        formik.setFieldValue(key, value, false);
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/contactIconColor.svg" alt="new contact icon" />
                        <span>New Contact</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            The Contact Name, Title, Email, Mobile Phone, and Address fields are required.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="contactName"
                                name="contactName"
                                type="text"
                                value={formik.values.contactName}
                                label="Contact Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.contactName) && Boolean(formik.touched.contactName)}
                                helperText={Boolean(formik.touched.contactName) && formik.errors.contactName}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="title"
                                name="title"
                                type="text"
                                value={formik.values.title}
                                label="Title"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.title) && Boolean(formik.touched.title)}
                                helperText={Boolean(formik.touched.title) && formik.errors.title}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="email"
                                name="email"
                                type="text"
                                value={formik.values.email}
                                label="Email"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.email) && Boolean(formik.touched.email)}
                                helperText={Boolean(formik.touched.email) && formik.errors.email}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <FormLabel
                                classes={{
                                    error: classes.phoneLabelError,
                                    root:  classes.phoneLabel
                                }}
                                focused={false}
                            >
                                Mobile Phone
                            </FormLabel>
                            <BobPhoneInput
                                name="mobilePhone"
                                label="Mobile Phone"
                                data-cy="user-phone"
                                value={formik.values.mobilePhone}
                                onChange={
                                    (v, d, e, fv) =>  { 
                                        setMobilePhoneCountry(d);
                                        handlePhoneNoChange(v, 'mobilePhone'); 
                                    }
                                }
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.mobilePhone) && Boolean(formik.touched.mobilePhone)}
                                helperText={Boolean(formik.touched.mobilePhone) && formik.errors.mobilePhone}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <FormLabel
                                classes={{
                                    error: classes.phoneLabelError,
                                    root:  classes.phoneLabel
                                }}
                                focused={false}
                            >
                                Office Phone
                            </FormLabel>
                            <BobPhoneInput
                                name="officePhone"
                                label="Office Phone"
                                data-cy="user-phone"
                                value={formik.values.officePhone}
                                onChange={
                                    (v, d, e, fv) => { 
                                        setOfficePhoneCountry(d);
                                        handlePhoneNoChange(v, 'officePhone');
                                    }
                                }
                                error={Boolean(formik.errors.officePhone) && Boolean(formik.touched.officePhone)}
                                helperText={Boolean(formik.touched.officePhone) && formik.errors.officePhone}
                            />
                        </FormControl>
                        <p className={classes.addressSearchText}><i className="fa fa-info-circle" aria-hidden="true" /><span className={classes.addressSpan}>Enter the physical address used to fill the address fields below.</span></p>
                        <PlacesAutocomplete
                            value={formik.values.addressSearch}
                            onChange={address => formik.setFieldValue('addressSearch', address)}
                            onSelect={(handleGeocodeAddress)}
                        >
                            {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                                <React.Fragment>
                                    <FormControl className={classes.inputWrapper}>
                                        <InputLabel
                                            htmlFor="addressSearch"
                                            classes={{
                                                error:   classes.error,
                                                focused: classes.labelFocused,
                                                root:    classes.label
                                            }}
                                            error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                        >
                                            Search for Address<span className={classes.required}> *</span>
                                        </InputLabel>
                                        <Input
                                            {...getInputProps({
                                                name: 'addressSearch',
                                                id:   'addressSearch'
                                            })}
                                            classes={{
                                                error:     classes.cssErrorUnderline,
                                                underline: classes.cssUnderline,
                                                root:      classes.input
                                            }}
                                            error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                        />
                                        {formik.touched.addressSearch &&
                                         formik.errors.addressSearch && <FormHelperText className={classes.error}>{formik.errors.addressSearch}</FormHelperText>}
                                    </FormControl>
                                    {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                        <List>
                                            {suggestions.map((suggestion, index) => {
                                                return (
                                                    <div key={index}>
                                                        <ListItem
                                                            button
                                                            className={classes.autoCompleteListItem}
                                                            {...getSuggestionItemProps(suggestion)}
                                                        >
                                                            <ListItemText primary={suggestion.description} />
                                                        </ListItem>
                                                    </div>
                                                );
                                            })}
                                        </List>
                                        <GoogleLogo />
                                    </Paper>}
                                </React.Fragment>
                            )}
                        </PlacesAutocomplete>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="address1"
                                name="address1"
                                type="text"
                                value={formik.values.address1}
                                label="Address 1"
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.address1) && Boolean(formik.touched.address1)}
                                helperText={Boolean(formik.touched.address1) && formik.errors.address1}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="address2"
                                name="address2"
                                type="text"
                                value={formik.values.address2}
                                label="Address 2"
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.address2) && Boolean(formik.touched.address2)}
                                helperText={Boolean(formik.touched.address2) && formik.errors.address2}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="city"
                                name="city"
                                type="text"
                                value={formik.values.city}
                                label="City"
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.city) && Boolean(formik.touched.city)}
                                helperText={Boolean(formik.touched.city) && formik.errors.city}
                            />
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={`${classes.inputWrapper} ${classes.state}`}>
                                <TextField
                                    id="state"
                                    name="state"
                                    type="text"
                                    value={formik.values.state}
                                    label="State"
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.state) && Boolean(formik.touched.state)}
                                    helperText={Boolean(formik.touched.state) && formik.errors.state}
                                />
                            </FormControl>
                            <FormControl className={`${classes.inputWrapper} ${classes.zip}`}>
                                <TextField
                                    id="zip"
                                    name="zip"
                                    type="text"
                                    value={formik.values.zip}
                                    label="Zip"
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.zip) && Boolean(formik.touched.zip)}
                                    helperText={Boolean(formik.touched.zip) && formik.errors.zip}
                                />
                            </FormControl>
                        </div>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateContactModal.defaultProps = {
    close:        () => {},
    companyInfo:  {},
    open:         false,
    selectedNode: {},
    title:        ''
};

CreateContactModal.propTypes = {
    close:        func.isRequired,
    companyInfo:  object,
    open:         bool.isRequired,
    selectedNode: object,
    title:        string.isRequired
};

const mapStateToProps = state => {
    return {
        companyInfo:  state.companyInfo,
        errors:       state.messages.errors,
        isLoading:    state.ui.isLoading,
        selectedNode: state.architect.selectedNode
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateContactModal);
